import { type DeviceAdapter, type RawDevice } from '../../platform/device/DeviceAdapter';

const ANDROID_TABLET_VIEWPORT_MIN = 600;

export const DeviceType = {
  ANDROID: 'Android',
  ANDROID_TABLET: 'Android Tablet',
  IPHONE: 'iPhone',
  IPAD: 'iPad',
  DESKTOP: 'Desktop',
  OTHER: 'Other',
} as const;
export type DeviceType = (typeof DeviceType)[keyof typeof DeviceType];

const mobileDeviceTypes: DeviceType[] = [DeviceType.ANDROID, DeviceType.IPHONE];

const DESKTOP_OS_PATTERNS = [/Windows NT|Windows/i, /Mac OS X|Macintosh/i, /Linux/i, /CrOS/i];

function isKnownDesktopOS(userAgent: string): boolean {
  return DESKTOP_OS_PATTERNS.some((pattern) => pattern.test(userAgent));
}

function isIpadOS13Plus(device: RawDevice): boolean {
  return device.platform === 'MacIntel' && device.maxTouchPoints > 1;
}

export const getDeviceType: (device: RawDevice) => DeviceType = (device) => {
  const userAgent = device.userAgent;

  if (/Android/i.test(userAgent)) {
    return isProbablyHandheldDevice(device) ? DeviceType.ANDROID : DeviceType.ANDROID_TABLET;
  }

  if (/iPad/i.test(userAgent) || isIpadOS13Plus(device)) {
    return DeviceType.IPAD;
  }

  if (/iPhone/i.test(userAgent)) {
    return DeviceType.IPHONE;
  }

  if (isKnownDesktopOS(userAgent)) {
    return DeviceType.DESKTOP;
  }

  return DeviceType.OTHER;
};

const isMobile = (device: RawDevice): boolean => {
  const deviceType = getDeviceType(device);
  if (!mobileDeviceTypes.includes(deviceType)) {
    return false;
  }
  return isProbablyHandheldDevice(device);
};

export const isSamsungInternetBrowser = (device: RawDevice): boolean =>
  /SamsungBrowser/i.test(device.userAgent);

const isAndroid = (device: RawDevice): boolean => getDeviceType(device) === DeviceType.ANDROID;

const WEBVIEW_UA_PATTERNS = [
  /wv/i,
  /Instagram/i,
  /FBAN|FBAV/i,
  /Line\//i,
  /Snapchat/i,
  /Twitter/i,
  /WhatsApp/i,
  /LinkedInApp/i,
  /BytedanceWebview|TikTok/i,
];

const isWebView = (device: RawDevice): boolean => {
  const ua = device.userAgent;
  return WEBVIEW_UA_PATTERNS.some((pattern) => pattern.test(ua));
};

const isProbablyHandheldDevice = (device: RawDevice): boolean => {
  const viewport = device.viewport;
  const width = Math.min(viewport.width, viewport.height);
  return width < ANDROID_TABLET_VIEWPORT_MIN;
};

export const createDevice = (raw: RawDevice): DeviceAdapter => ({
  get userAgent() {
    return raw.userAgent;
  },
  get platform() {
    return raw.platform;
  },
  get maxTouchPoints() {
    return raw.maxTouchPoints;
  },
  get viewport() {
    return raw.viewport;
  },
  get isMobile() {
    return isMobile(raw);
  },
  get isAndroid() {
    return isAndroid(raw);
  },
  get isWebView() {
    return isWebView(raw);
  },
});
